import React, { useEffect, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X } from 'lucide-react';
import { getConfig } from '../../config/production';

// Module-level cache so we only load Stripe.js once across the entire app.
let stripePromise;
function getStripePromise() {
  if (!stripePromise) {
    const key = getConfig('STRIPE_PUBLISHABLE_KEY');
    if (!key) {
      console.error('[Stripe] STRIPE_PUBLISHABLE_KEY is not configured');
      return null;
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// Renders Stripe's Embedded Checkout inside a JPRO-styled modal. Stripe owns
// the iframe content; we own the chrome (header, close button, success/error
// surfacing). The parent passes a clientSecret obtained from the backend's
// POST /billing/checkout response.
//
// Lifecycle:
//   - clientSecret is null → modal is closed (do not mount provider).
//   - clientSecret set → mount EmbeddedCheckoutProvider with it. Provider is
//     keyed by clientSecret so a new session forces a fresh mount (the SDK
//     refuses to swap clientSecret on a live provider).
//   - User completes payment → onComplete fires → parent closes the modal
//     and refetches subscriptions.
//   - User dismisses → parent clears clientSecret to unmount.
export default function EmbeddedCheckoutModal({ clientSecret, onClose, onComplete }) {
  const stripeReady = useMemo(() => getStripePromise(), []);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Block body scroll while the modal is open.
  useEffect(() => {
    if (!clientSecret) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [clientSecret]);

  // Esc-to-close — same affordance as other JPRO modals.
  useEffect(() => {
    if (!clientSecret) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [clientSecret, onClose]);

  if (!clientSecret) return null;

  if (!stripeReady) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="max-w-md rounded-lg bg-white p-6 dark:bg-gray-900">
          <p className="text-red-600 dark:text-red-400">
            Payments are not configured. Please contact support.
          </p>
          <button
            onClick={onClose}
            className="mt-4 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative flex max-h-[95vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Complete your subscription
          </h2>
          <button
            onClick={onClose}
            aria-label="Close checkout"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-2">
          <EmbeddedCheckoutProvider
            key={clientSecret}
            stripe={stripeReady}
            options={{
              clientSecret,
              onComplete: () => onCompleteRef.current?.(),
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}
